/**
 * Zod schemas and inferred DTO types for every HTTP request body the API
 * accepts, plus the provider-neutral gateway chat shapes. Routes import these to
 * validate input; the web client imports the inferred types.
 */
import { z } from 'zod';
import { API_KEY_PROVIDERS, ASSET_PURPOSES, BUILD_PLATFORMS, CONSENT_METHODS, CREATION_MODES, CREDENTIAL_SCOPES, FUNDING_PROVIDERS, HATS, IMAGE_MIMES, INVITE_CODE_KINDS, MAX_FILE_BYTES, MESSAGE_SENDERS, MODERATION_STATUSES, NOTIFICATION_KINDS, OAUTH_PROVIDERS, OPENAI_OAUTH_TIERS, PROJECT_TYPES, PUBLISH_STATES, REVIEW_MODES, REVIEW_REQUEST_STATUSES, ROLES, SESSION_MODES, SESSION_STATUSES, UPLOAD_MIME_ALLOWLIST, USER_STATUSES, VIRTUAL_KEY_STATUSES, } from './constants.js';
import { ERROR_CODES } from './errors.js';
import { usernameRejectMessage, validateUsername } from './username.js';
/* -------------------------------------------------------------------------- */
/* Reusable enum schemas (wrap the constant tuples)                           */
/* -------------------------------------------------------------------------- */
export const roleSchema = z.enum(ROLES);
export const userStatusSchema = z.enum(USER_STATUSES);
export const hatSchema = z.enum(HATS);
export const inviteCodeKindSchema = z.enum(INVITE_CODE_KINDS);
export const projectTypeSchema = z.enum(PROJECT_TYPES);
export const creationModeSchema = z.enum(CREATION_MODES);
export const sessionModeSchema = z.enum(SESSION_MODES);
export const sessionStatusSchema = z.enum(SESSION_STATUSES);
export const publishStateSchema = z.enum(PUBLISH_STATES);
export const reviewModeSchema = z.enum(REVIEW_MODES);
export const reviewRequestStatusSchema = z.enum(REVIEW_REQUEST_STATUSES);
export const fundingProviderSchema = z.enum(FUNDING_PROVIDERS);
export const apiKeyProviderSchema = z.enum(API_KEY_PROVIDERS);
export const oauthProviderSchema = z.enum(OAUTH_PROVIDERS);
export const openAiOAuthTierSchema = z.enum(OPENAI_OAUTH_TIERS);
export const credentialScopeSchema = z.enum(CREDENTIAL_SCOPES);
export const assetPurposeSchema = z.enum(ASSET_PURPOSES);
export const moderationStatusSchema = z.enum(MODERATION_STATUSES);
export const messageSenderSchema = z.enum(MESSAGE_SENDERS);
export const consentMethodSchema = z.enum(CONSENT_METHODS);
export const notificationKindSchema = z.enum(NOTIFICATION_KINDS);
export const buildPlatformSchema = z.enum(BUILD_PLATFORMS);
export const virtualKeyStatusSchema = z.enum(VIRTUAL_KEY_STATUSES);
export const uploadMimeSchema = z.enum(UPLOAD_MIME_ALLOWLIST);
export const errorCodeSchema = z.enum(ERROR_CODES);
/* -------------------------------------------------------------------------- */
/* Reusable primitives                                                        */
/* -------------------------------------------------------------------------- */
export const uuidSchema = z.string().uuid();
export const emailSchema = z.string().email().max(254);
export const passwordSchema = z.string().min(8).max(200);
export const nameSchema = z.string().min(1).max(80);
export const birthYearSchema = z.number().int().gte(1900).lte(2100);
/**
 * A username at signup: full validation (length, charset, reserved list,
 * profanity) via validateUsername, surfaced as a friendly message on failure.
 */
export const usernameSchema = z.string().superRefine((val, ctx) => {
    const result = validateUsername(val);
    if (!result.valid) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: usernameRejectMessage(result) ?? 'Please pick a different username.',
        });
    }
});
/**
 * A login identifier: a username OR an email in one field. Kept loose on
 * purpose (the account lookup decides which it is); only length is bounded.
 */
export const identifierSchema = z.string().min(1).max(254);
export const jsonValueSchema = z.lazy(() => z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(jsonValueSchema),
]));
/* -------------------------------------------------------------------------- */
/* Auth: signup, login, session                                              */
/* -------------------------------------------------------------------------- */
export const signupStudentSchema = z.object({
    kind: z.literal('student'),
    name: nameSchema,
    username: usernameSchema,
    /** Optional login email. Not the recovery address. */
    email: emailSchema.optional(),
    /**
     * Required recovery address for the ProDevGroup mailbox. Not unique: several
     * kids can share a parent's address. Never stored in users.email.
     */
    recoveryEmail: emailSchema,
    password: passwordSchema,
    birthYear: birthYearSchema,
    /** Optional link to a parent already on the platform. */
    parentEmail: emailSchema.optional(),
    /** Optional preferred mentor. */
    mentorEmail: emailSchema.optional(),
    /** Optional parent invite code (the email-less kid linking path). */
    inviteCode: z.string().min(1).max(40).optional(),
});
export const signupParentSchema = z.object({
    kind: z.literal('parent'),
    name: nameSchema,
    username: usernameSchema,
    email: emailSchema.optional(),
    /** Required recovery address. Not stored in users.email. */
    recoveryEmail: emailSchema,
    password: passwordSchema,
});
export const signupMentorSchema = z.object({
    kind: z.literal('mentor'),
    name: nameSchema,
    username: usernameSchema,
    email: emailSchema.optional(),
    /** Required recovery address. Not stored in users.email. */
    recoveryEmail: emailSchema,
    password: passwordSchema,
});
export const signupAdultSelfServeSchema = z.object({
    kind: z.literal('adult_self_serve'),
    name: nameSchema,
    username: usernameSchema,
    email: emailSchema.optional(),
    /** Required recovery address. Not stored in users.email. */
    recoveryEmail: emailSchema,
    password: passwordSchema,
    birthYear: birthYearSchema,
});
export const signupSchema = z.discriminatedUnion('kind', [
    signupStudentSchema,
    signupParentSchema,
    signupMentorSchema,
    signupAdultSelfServeSchema,
]);
/** Login accepts a username or an email in one identifier field. */
export const loginSchema = z.object({
    identifier: identifierSchema,
    password: z.string().min(1).max(200),
});
/**
 * The account's capabilities, carried on the me/session payload. Capabilities
 * attach to facts about the account, not to a single role (SPEC.md "One person,
 * multiple hats"), so one login can be a builder, a mentor, and a parent at
 * once. Derived by deriveCapabilities in capabilities.ts; this schema is the
 * single source of truth for the shape.
 */
export const capabilitiesSchema = z.object({
    /** Can create projects and run sessions of its own. */
    canBuild: z.boolean(),
    /** Has linked kids, so the parent surfaces apply. */
    isParent: z.boolean(),
    /** Mentor-role account, so the mentor surfaces apply. */
    isMentor: z.boolean(),
    /** The hats to show in the top-nav switcher, in nav order. */
    hats: z.array(hatSchema),
});
/** The authenticated user as returned by /api/accounts/me and login. */
export const sessionUserSchema = z.object({
    id: uuidSchema,
    role: roleSchema,
    name: nameSchema,
    /** Unique handle, always present. */
    username: z.string(),
    /** Null when the account has no email on file. */
    email: emailSchema.nullable(),
    status: userStatusSchema,
    isSelfServe: z.boolean(),
    birthYear: z.number().int().nullable(),
    parentId: uuidSchema.nullable(),
    mentorId: uuidSchema.nullable(),
    /** What this login can do and which hats to show. */
    capabilities: capabilitiesSchema,
});
export const authResponseSchema = z.object({ user: sessionUserSchema });
/* -------------------------------------------------------------------------- */
/* Account merge, invite codes, password reset (identity workflows)           */
/* -------------------------------------------------------------------------- */
/**
 * Prove ownership of a second account to merge it into the one you are logged
 * into (SPEC.md "Account merge workflow"). otherIdentifier is that account's
 * username or email; otherPassword is its password. Same-role merges only,
 * enforced server-side.
 */
export const accountMergeSchema = z.object({
    otherIdentifier: identifierSchema,
    otherPassword: z.string().min(1).max(200),
});
/**
 * A parent mints an invite code for parent-kid linking. Optionally pre-targets
 * an existing email-less kid, and optionally sets a custom lifetime.
 */
export const inviteCodeCreateSchema = z.object({
    kind: inviteCodeKindSchema.optional(),
    /** Pre-target a specific existing kid, or omit for an open code. */
    studentId: uuidSchema.nullish(),
    /** Lifetime in hours (default decided server-side). */
    expiresInHours: z.number().int().min(1).max(720).optional(),
});
/** A kid (or their account) claims a parent invite code to link to the parent. */
export const inviteCodeClaimSchema = z.object({
    code: z.string().min(1).max(40),
});
/**
 * Start the email password-reset flow. The identifier is a username or email;
 * the server sends a reset email only when an email is on file. Email-less kids
 * get "ask your parent or mentor" instead.
 */
export const passwordResetRequestSchema = z.object({
    identifier: identifierSchema,
});
/** Finish the email password-reset flow with the emailed token. */
export const passwordResetCompleteSchema = z.object({
    token: z.string().min(1).max(200),
    newPassword: passwordSchema,
});
/**
 * A parent or mentor resets a linked kid's password directly (the path for
 * email-less kids). Authorization (parent-of / assigned-mentor) is enforced
 * server-side.
 */
export const resetKidPasswordSchema = z.object({
    studentId: uuidSchema,
    newPassword: passwordSchema,
});
/* -------------------------------------------------------------------------- */
/* Consents                                                                   */
/* -------------------------------------------------------------------------- */
export const consentCreateSchema = z.object({
    studentId: uuidSchema,
    method: consentMethodSchema,
});
/* -------------------------------------------------------------------------- */
/* Projects                                                                   */
/* -------------------------------------------------------------------------- */
export const projectCreateSchema = z.object({
    name: z.string().min(1).max(60),
    type: projectTypeSchema,
    /** What the student typed they want to build. */
    prompt: z.string().min(1).max(4000),
    instructions: z.string().max(4000).optional(),
    reviewMode: reviewModeSchema.optional(),
    /** Sketch-to-app: an already-uploaded sketch image to scaffold from. */
    sketchAssetId: uuidSchema.optional(),
});
export const projectUpdateSchema = z
    .object({
    name: z.string().min(1).max(60).optional(),
    instructions: z.string().max(4000).optional(),
    reviewMode: reviewModeSchema.optional(),
    galleryOptIn: z.boolean().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update.' });
export const slugCheckSchema = z.object({ slug: z.string().min(1).max(64) });
/* -------------------------------------------------------------------------- */
/* Assets (file bank)                                                         */
/* -------------------------------------------------------------------------- */
export const assetUploadMetaSchema = z.object({
    filename: z.string().min(1).max(200),
    mime: uploadMimeSchema,
    purpose: assetPurposeSchema,
    sizeBytes: z.number().int().positive().max(MAX_FILE_BYTES),
});
export const assetUpdateSchema = z.object({ purpose: assetPurposeSchema });
/* -------------------------------------------------------------------------- */
/* Funding sources                                                            */
/* -------------------------------------------------------------------------- */
const fundingScopeFields = {
    label: z.string().min(1).max(80),
    scope: credentialScopeSchema,
    projectId: uuidSchema.nullish(),
};
export const fundingSourceApiKeyCreateSchema = z.object({
    kind: z.literal('api_key'),
    provider: apiKeyProviderSchema,
    apiKey: z.string().min(8).max(400),
    ...fundingScopeFields,
});
export const fundingSourceOAuthCreateSchema = z.object({
    kind: z.literal('oauth'),
    provider: oauthProviderSchema,
    /** Only meaningful for the openai provider. */
    modelTier: openAiOAuthTierSchema.nullish(),
    ...fundingScopeFields,
});
export const fundingSourceCreateSchema = z
    .discriminatedUnion('kind', [fundingSourceApiKeyCreateSchema, fundingSourceOAuthCreateSchema])
    .superRefine((val, ctx) => {
    if (val.scope === 'project' && !val.projectId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['projectId'],
            message: 'projectId is required when scope is project.',
        });
    }
    if (val.kind === 'oauth' && val.provider !== 'openai' && val.modelTier) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['modelTier'],
            message: 'modelTier only applies to the openai provider.',
        });
    }
});
export const fundingSourceUpdateSchema = z
    .object({
    label: z.string().min(1).max(80).optional(),
    scope: credentialScopeSchema.optional(),
    projectId: uuidSchema.nullish(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update.' });
/* -------------------------------------------------------------------------- */
/* Virtual keys                                                               */
/* -------------------------------------------------------------------------- */
export const virtualKeyLimitsSchema = z.object({
    rpmLimit: z.number().int().positive(),
    tokensPerDay: z.number().int().positive(),
    dollarsPerMonth: z.number().positive(),
    allowedModels: z.array(z.string()).nullish(),
});
export const virtualKeyCreateSchema = virtualKeyLimitsSchema.extend({
    userId: uuidSchema,
    fundingSourceId: uuidSchema,
    scopes: z.array(z.string()).default([]),
});
export const virtualKeyUpdateSchema = z
    .object({
    rpmLimit: z.number().int().positive().optional(),
    tokensPerDay: z.number().int().positive().optional(),
    dollarsPerMonth: z.number().positive().optional(),
    allowedModels: z.array(z.string()).nullish(),
    scopes: z.array(z.string()).optional(),
    status: virtualKeyStatusSchema.optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update.' });
/* -------------------------------------------------------------------------- */
/* Mentor actions                                                             */
/* -------------------------------------------------------------------------- */
export const approveStudentSchema = virtualKeyLimitsSchema.extend({
    studentId: uuidSchema,
    fundingSourceId: uuidSchema,
});
export const rejectStudentSchema = z.object({
    studentId: uuidSchema,
    reason: z.string().max(500).optional(),
});
export const pauseStudentSchema = z.object({
    studentId: uuidSchema,
    paused: z.boolean(),
});
export const pauseVirtualKeySchema = z.object({
    virtualKeyId: uuidSchema,
    paused: z.boolean(),
});
export const assignMentorSchema = z.object({
    studentId: uuidSchema,
    mentorId: uuidSchema,
    isPrimary: z.boolean().optional(),
});
export const flagResolveSchema = z.object({
    status: z.literal('reviewed'),
});
/* -------------------------------------------------------------------------- */
/* Messaging (ask my developer / mentor replies)                             */
/* -------------------------------------------------------------------------- */
export const mentorMessageCreateSchema = z.object({
    studentId: uuidSchema,
    body: z.string().min(1).max(4000),
    /** Optional reference to the session the message is about. */
    sessionRef: uuidSchema.nullish(),
});
/* -------------------------------------------------------------------------- */
/* Publishing                                                                 */
/* -------------------------------------------------------------------------- */
export const publishRequestSchema = z.object({
    /** Optionally set a lock password at publish time. Null or omitted means open. */
    lockPassword: z.string().min(1).max(100).nullish(),
});
export const publishLockSchema = z.object({
    /** New password, or null to remove the lock. */
    password: z.string().min(1).max(100).nullable(),
});
export const publishUnlockSchema = z.object({
    password: z.string().min(1).max(100),
});
/* -------------------------------------------------------------------------- */
/* Gateway chat (provider-neutral)                                            */
/* -------------------------------------------------------------------------- */
export const textPartSchema = z.object({
    type: z.literal('text'),
    text: z.string(),
});
export const imagePartSchema = z.object({
    type: z.literal('image'),
    /** MIME type of the image bytes (image/png, image/jpeg, ...). */
    mimeType: z.string(),
    /** Base64-encoded image bytes. */
    data: z.string(),
});
export const toolUsePartSchema = z.object({
    type: z.literal('tool_use'),
    id: z.string(),
    name: z.string(),
    input: z.record(z.unknown()),
});
/**
 * An extended-thinking block from a reasoning model (Kimi K3, MiniMax M3, and
 * Claude with thinking enabled). The provider streams these before the visible
 * text and any tool_use. The block MUST be carried back verbatim, signature and
 * all, in the assistant turn that a follow-up tool_result answers: the
 * Anthropic-format API rejects or mishandles a tool_use whose turn is missing
 * its preceding thinking block, which sends the model into an endless
 * thinking-only loop that emits no tool call. `signature` may be empty for a
 * provider that does not sign its thinking, but is preserved when present.
 */
export const thinkingPartSchema = z.object({
    type: z.literal('thinking'),
    thinking: z.string(),
    signature: z.string().default(''),
});
export const toolResultPartSchema = z.object({
    type: z.literal('tool_result'),
    toolUseId: z.string(),
    content: z.string(),
    isError: z.boolean().optional(),
});
export const contentPartSchema = z.discriminatedUnion('type', [
    textPartSchema,
    imagePartSchema,
    thinkingPartSchema,
    toolUsePartSchema,
    toolResultPartSchema,
]);
export const gatewayMessageSchema = z.object({
    role: z.enum(['user', 'assistant']),
    content: z.array(contentPartSchema),
});
export const gatewayToolSchema = z.object({
    name: z.string(),
    description: z.string().optional(),
    /** JSON Schema object describing the tool's input. */
    inputSchema: z.record(z.unknown()),
});
export const gatewayChatRequestSchema = z.object({
    model: z.string(),
    system: z.string().optional(),
    messages: z.array(gatewayMessageSchema).min(1),
    tools: z.array(gatewayToolSchema).optional(),
    maxTokens: z.number().int().min(1).max(1_000_000),
    temperature: z.number().min(0).max(2).optional(),
});
export const streamStopReasonSchema = z.enum([
    'end_turn',
    'tool_use',
    'max_tokens',
    'stop',
    'error',
]);
export const textDeltaEventSchema = z.object({
    type: z.literal('text_delta'),
    text: z.string(),
});
export const toolUseEventSchema = z.object({
    type: z.literal('tool_use'),
    id: z.string(),
    name: z.string(),
    input: z.record(z.unknown()),
});
/**
 * One complete extended-thinking block, emitted once the provider closes it (the
 * text and signature are already accumulated). The runner keeps it as a
 * `thinking` content part at the head of the assistant turn so the next
 * tool_result request carries it back verbatim.
 */
export const thinkingEventSchema = z.object({
    type: z.literal('thinking'),
    thinking: z.string(),
    signature: z.string().default(''),
});
export const usageEventSchema = z.object({
    type: z.literal('usage'),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative(),
});
export const doneEventSchema = z.object({
    type: z.literal('done'),
    stopReason: streamStopReasonSchema.optional(),
});
export const errorEventSchema = z.object({
    type: z.literal('error'),
    code: errorCodeSchema,
    message: z.string(),
    resetsAt: z.string().optional(),
});
export const gatewayStreamEventSchema = z.discriminatedUnion('type', [
    textDeltaEventSchema,
    toolUseEventSchema,
    thinkingEventSchema,
    usageEventSchema,
    doneEventSchema,
    errorEventSchema,
]);
/* -------------------------------------------------------------------------- */
/* Image generation                                                           */
/* -------------------------------------------------------------------------- */
export const imageGenerateRequestSchema = z.object({
    projectId: uuidSchema,
    prompt: z.string().min(1).max(1000),
    size: z.enum(['256x256', '512x512', '1024x1024']).optional(),
    n: z.number().int().min(1).max(4).optional(),
    /** Optional filename for the saved asset. */
    filename: z.string().max(200).optional(),
});
export const imageGenerateResultSchema = z.object({
    assetIds: z.array(uuidSchema),
    moderation: moderationStatusSchema,
});
/* -------------------------------------------------------------------------- */
/* Project data API                                                           */
/* -------------------------------------------------------------------------- */
const collectionSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9_-]+$/, 'letters, numbers, dashes, and underscores only');
const recordKeySchema = z.string().min(1).max(128);
export const dataRecordUpsertSchema = z.object({
    collection: collectionSchema,
    key: recordKeySchema,
    value: jsonValueSchema,
});
export const dataRecordValueSchema = z.object({ value: jsonValueSchema });
export const dataQuerySchema = z.object({
    collection: collectionSchema,
    limit: z.number().int().min(1).max(200).optional(),
});
/* -------------------------------------------------------------------------- */
/* Badges                                                                     */
/* -------------------------------------------------------------------------- */
export const badgeSchema = z.object({
    code: z.string(),
    name: z.string(),
    description: z.string(),
    icon: z.string(),
});
export const badgeAwardSchema = z.object({
    badgeCode: z.string(),
    awardedAt: z.string(),
    context: jsonValueSchema.nullable(),
});
/* -------------------------------------------------------------------------- */
/* Gallery                                                                    */
/* -------------------------------------------------------------------------- */
export const galleryOptInSchema = z.object({ optIn: z.boolean() });
export const galleryApproveSchema = z.object({
    projectId: uuidSchema,
    screenshotPath: z.string().max(400).optional(),
});
export const galleryEntrySchema = z.object({
    slug: z.string(),
    title: z.string(),
    authorFirstName: z.string(),
    screenshotPath: z.string().nullable(),
});
/* -------------------------------------------------------------------------- */
/* Builds (EAS)                                                               */
/* -------------------------------------------------------------------------- */
export const buildCreateSchema = z.object({
    projectId: uuidSchema,
    platform: buildPlatformSchema,
});
/* -------------------------------------------------------------------------- */
/* Custom domains                                                             */
/* -------------------------------------------------------------------------- */
const domainSchema = z
    .string()
    .min(4)
    .max(253)
    .regex(/^(?!-)[a-z0-9-]{1,63}(?<!-)(\.(?!-)[a-z0-9-]{1,63}(?<!-))+$/i, 'must be a valid domain name');
export const customDomainCreateSchema = z.object({
    projectId: uuidSchema,
    domain: domainSchema,
});
/* -------------------------------------------------------------------------- */
/* Review requests (pull-request flow)                                        */
/* -------------------------------------------------------------------------- */
export const reviewRequestCreateSchema = z.object({
    projectId: uuidSchema,
    branch: z.string().min(1).max(120).default('work'),
});
export const reviewRequestUpdateSchema = z.object({
    status: reviewRequestStatusSchema,
});
/* -------------------------------------------------------------------------- */
/* Notifications                                                              */
/* -------------------------------------------------------------------------- */
export const notificationMarkReadSchema = z
    .object({
    ids: z.array(uuidSchema).optional(),
    all: z.boolean().optional(),
})
    .refine((v) => v.all === true || (v.ids?.length ?? 0) > 0, {
    message: 'Provide ids or all=true.',
});
export const notificationDtoSchema = z.object({
    id: uuidSchema,
    kind: notificationKindSchema,
    body: z.string(),
    href: z.string().nullable(),
    readAt: z.string().nullable(),
    createdAt: z.string(),
});
/* -------------------------------------------------------------------------- */
/* OAuth connect flow (subscription funding)                                  */
/* -------------------------------------------------------------------------- */
export const oauthStartSchema = z
    .object({
    provider: oauthProviderSchema,
    modelTier: openAiOAuthTierSchema.nullish(),
    label: z.string().min(1).max(80),
    scope: credentialScopeSchema,
    projectId: uuidSchema.nullish(),
})
    .superRefine((val, ctx) => {
    if (val.scope === 'project' && !val.projectId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['projectId'],
            message: 'projectId is required when scope is project.',
        });
    }
    if (val.provider !== 'openai' && val.modelTier) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['modelTier'],
            message: 'modelTier only applies to the openai provider.',
        });
    }
});
export const oauthStartResponseSchema = z.object({
    /** The provider authorization URL to redirect the owner to. */
    authorizeUrl: z.string().url(),
    state: z.string(),
});
export const oauthCallbackSchema = z.object({
    code: z.string().min(1),
    state: z.string().min(1),
});
/* -------------------------------------------------------------------------- */
/* Usage reporting                                                            */
/* -------------------------------------------------------------------------- */
export const usageSummarySchema = z.object({
    windowStart: z.string(),
    windowEnd: z.string(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative(),
    requestCount: z.number().int().nonnegative(),
});
/** Convenience: every image MIME as a runtime array (used in upload UIs). */
export const IMAGE_MIME_VALUES = IMAGE_MIMES;
//# sourceMappingURL=api-types.js.map